import { timestampDate } from '@bufbuild/protobuf/wkt';
import {
  DeploymentType,
  WorkspaceStatus,
} from '@sunstone/api-client/gen/deployments/v1/deployments_pb';
import {
  DeleteMode,
  useDeleteWorkspace,
  useGetDeployment,
  useGetLastWorkspaceWorkflowRun,
  useGetServiceHealthStatus,
  useGetWorkspace,
  useInfiniteListAuditEvents,
  useListAvailableReleases,
  useResolveComponentFamilies,
  useRetryWorkspaceProvisioning,
  useStreamWorkspaceEvents,
} from '@sunstone/api-client/hooks';
import { Button, Card, CardContent, Tabs, TabsContent, TabsList, TabsTrigger } from '@sunstone/ui';
import { ExternalLink, Loader2, RotateCcw } from 'lucide-react';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { toast } from 'sonner';
import { AuditHistory } from '@/components/ui/AuditHistory';
import { ComponentsDiffPanel } from '@/components/ui/ComponentsDiffPanel';
import { DeleteConfirmationDialog } from '@/components/ui/DeleteConfirmationDialog';
import { DeployBaisDialog } from '@/components/ui/DeployBaisDialog';
import { DeployedComponentsPanel } from '@/components/ui/DeployedComponentsPanel';
import { DeployMaiDialog } from '@/components/ui/DeployMaiDialog';
import { ServiceHealthPanel } from '@/components/ui/ServiceHealthPanel';
import { SingleServiceDeployDialog } from '@/components/ui/SingleServiceDeployDialog';
import { StatusBadge } from '@/components/ui/StatusBadge';
import { WorkflowRunBanner } from '@/components/ui/WorkflowRunBanner';
import { useAuthToken } from '@/features/auth';
import { useAuthorization } from '@/features/auth/useAuthorization';
import type { ComponentFamily } from '@/features/workspaces/componentFamily';
import { getReleaseActionGating } from '@/features/workspaces/releaseActionGating';
import {
  ENV_LABELS,
  workspaceEnvironmentKey,
  workspaceStatusKey,
} from '@/features/workspaces/utils';
import {
  type DeployDialog,
  WorkspaceDeployUpgradePanel,
} from '@/features/workspaces/WorkspaceDeployUpgradePanel';
import { WorkspaceVersionStatus } from '@/features/workspaces/WorkspaceVersionStatus';
import { MOCK_DUPLOAI_TENANTS } from '@/lib/mock/duploai';
import { formatDateTime } from '@/lib/time';

// ─── WorkspaceDetail ──────────────────────────────────────────────────────────

interface WorkspaceDetailProps {
  workspaceId: string;
  tenantId?: string;
  deleteOpen?: boolean;
  onDeleteOpenChange?: (open: boolean) => void;
  onPreviewRelease?: (releaseId: string) => void;
  isPreviewPending?: boolean;
}

type Tab = 'upgrade' | 'history' | 'health' | 'components';

export function WorkspaceDetail({
  workspaceId,
  tenantId,
  deleteOpen: propDeleteOpen,
  onDeleteOpenChange,
  onPreviewRelease,
  isPreviewPending = false,
}: WorkspaceDetailProps) {
  const { token } = useAuthToken();
  const { hasPermission } = useAuthorization();
  const navigate = useNavigate();
  useStreamWorkspaceEvents(workspaceId, token);
  const { data: workspaceData, isLoading, error } = useGetWorkspace(workspaceId, token);
  const {
    data: auditData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteListAuditEvents(token, 'workspace', workspaceId);
  const auditEvents = auditData?.pages.flatMap((p) => p.events) ?? [];
  const { data: availableReleasesData } = useListAvailableReleases(workspaceId, token, 'baic');
  const deleteWorkspace = useDeleteWorkspace(token);
  const retryProvisioning = useRetryWorkspaceProvisioning(token);
  const workspace = workspaceData?.workspace;
  const availableReleases = availableReleasesData?.releases ?? [];

  const { data: deploymentData } = useGetDeployment(workspace?.deploymentId ?? '', token);

  // On-prem workspaces are delivered by the Duplo AI extension engine rather than
  // x-forge-installer, so the engine-specific fields below differ. Promote and
  // upgrade remain available: driving a helm install through the extension engine
  // is the goal, which means the eventual Duplo client needs a write path for
  // TenantSpec.chartVersion plus lastRequestedAction.
  const isOnPrem = deploymentData?.deployment?.deploymentType === DeploymentType.ON_PREM;

  const [internalDeleteOpen, setInternalDeleteOpen] = useState(false);
  const deleteOpen = propDeleteOpen ?? internalDeleteOpen;
  const setDeleteOpen = onDeleteOpenChange ?? setInternalDeleteOpen;
  const [isDeleting, setIsDeleting] = useState(false);
  const [activeDeployDialog, setActiveDeployDialog] = useState<DeployDialog | null>(null);

  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab') as Tab | null;
  const hasUpgrade = availableReleases.length > 0;
  const availableTabs: Tab[] = ['components', 'health', 'upgrade', 'history'];
  const defaultTab: Tab = availableTabs[0] ?? 'components';
  const activeTab: Tab = tabParam && availableTabs.includes(tabParam) ? tabParam : defaultTab;
  // availableReleases is sorted newest-first by the backend, so the last
  // entry is the smallest version still greater than current - i.e. the
  // immediate next release, which is the only upgrade target we offer.
  const nextRelease = availableReleases[availableReleases.length - 1];

  const { data: serviceHealth, isLoading: serviceHealthLoading } = useGetServiceHealthStatus(
    token,
    workspaceId,
    activeTab === 'health',
  );

  const { data: lastWorkflowRunData } = useGetLastWorkspaceWorkflowRun(workspaceId, token);
  const lastWorkflowRun = lastWorkflowRunData?.found ? lastWorkflowRunData : undefined;

  const deployedServiceNames = workspace?.deployedReleaseManifest?.components.map((c) => c.service);
  const { data: componentFamiliesData } = useResolveComponentFamilies(token, deployedServiceNames);
  const familyByServiceName = componentFamiliesData?.familyByServiceName ?? {};

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16 text-muted-foreground">Loading...</div>
    );
  }

  if (error || !workspace) {
    return (
      <div className="flex items-center justify-center py-16 text-muted-foreground">
        Workspace not found.
      </div>
    );
  }

  // PreviewReleaseDiff (and PromoteRelease/RunReleasePlan downstream of it)
  // rejects outright when a workspace has no current_release_id (e.g. a
  // Duplo-discovered tenant whose apps.tfvars.json hasn't been backfilled
  // yet) - guard the entry point here too, same as WorkspacePage's
  // "Release Sync & Promote" button.
  const noCurrentRelease = !workspace.currentReleaseId;
  // Shared with WorkspacePage's "Release Sync & Promote" trigger - see
  // releaseActionGating.ts.
  const upgradeGating = getReleaseActionGating(workspace, {
    isPending: isPreviewPending,
    noCurrentRelease,
  });
  // Drives both the "Preview Upgrade to X" button and the divider above it -
  // computed once so the divider never renders with nothing below it.
  const showPreviewUpgradeButton =
    !!nextRelease &&
    !upgradeGating.disabled &&
    workspace.status !== WorkspaceStatus.PROVISIONING &&
    hasPermission('upgrade:workspace');

  // Shared between the Components tab's family empty-state CTA and the
  // Deploy & Upgrade tab's tiles - both open the same dialogs, so there's
  // one mechanism for "go deploy X" on this page, not two. BAIC has no
  // dedicated deploy action of its own (it's the base platform, only ever
  // changed via a full release upgrade) - no CTA for it, same as A8.
  function canDeployFamily(family: ComponentFamily): boolean {
    if (isWorkflowRunning) return false;
    switch (family) {
      case 'BAIC':
        return false;
      case 'CSAI':
        return hasPermission('deploy_csai:workspace');
      case 'MAI':
        return hasPermission('deploy_mai:workspace');
      case 'A8':
        return false;
    }
  }

  function handleDeployFamily(family: ComponentFamily) {
    if (family === 'CSAI') setActiveDeployDialog('bais');
    if (family === 'MAI') setActiveDeployDialog('mai');
  }

  const statusKey = workspaceStatusKey(workspace.status);
  const envKey = workspaceEnvironmentKey(workspace.environment);
  const envLabel = ENV_LABELS[envKey] ?? envKey.replace('WORKSPACE_ENVIRONMENT_', '');
  const workspaceSlug = workspace.slug ?? '';
  // Duplo reports this workspace's engine state. Fixture-backed until the
  // read-only DuploAI client lands; matched on slug because a Duplo BAIC tenant
  // name maps 1:1 to a Sunstone workspace slug.
  const duploTenant = isOnPrem
    ? MOCK_DUPLOAI_TENANTS.find((t) => t.name === workspaceSlug)
    : undefined;
  // Only the backend-confirmed value is shown, never a client-constructed guess
  // (see PR #326 review: a guessed link reproduces the SUN-471 bug class for any
  // workspace whose deployment domain isn't actually known yet). No link renders
  // until forge_ui_url is actually set.
  const forgeUrl = workspace.forgeUiUrl;

  const isWorkflowRunning = lastWorkflowRun?.status === 'in_progress';

  function handleDelete(mode: 'full' | 'force_tombstone') {
    setIsDeleting(true);
    const deleteMode = mode === 'force_tombstone' ? DeleteMode.FORCE_TOMBSTONE : DeleteMode.FULL;
    deleteWorkspace.mutate(
      { id: workspaceId, mode: deleteMode },
      {
        onSuccess: () => {
          setDeleteOpen(false);
          const deployId = workspace?.deploymentId ?? '';
          if (tenantId && deployId) {
            navigate(`/tenants/${tenantId}/deployments/${deployId}`);
          } else {
            navigate(-1);
          }
        },
        onSettled: () => setIsDeleting(false),
      },
    );
  }

  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
      <WorkflowRunBanner
        run={lastWorkflowRun}
        runningHint="Deploy and upgrade actions are disabled until this finishes."
      />

      {/* Metadata card — always visible */}
      <Card>
        <CardContent className="py-4">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div>
              <p className="text-xs text-muted-foreground mb-1 font-medium">URL Name</p>
              <p className="text-sm font-mono text-foreground">{workspace.slug || '—'}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1 font-medium">Status</p>
              <div className="flex items-center gap-2">
                <StatusBadge status={statusKey} />
                {statusKey === 'WORKSPACE_STATUS_PROVISIONING_FAILED' &&
                  hasPermission('create:workspace') && (
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={retryProvisioning.isPending}
                      onClick={() => {
                        retryProvisioning.mutate(
                          { id: workspaceId },
                          {
                            onSuccess: () =>
                              toast.success('Provisioning restarted — status will update shortly.'),
                            onError: (err) =>
                              toast.error(err.message ?? 'Failed to retry provisioning.'),
                          },
                        );
                      }}
                    >
                      {retryProvisioning.isPending ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Retrying…
                        </>
                      ) : (
                        <>
                          <RotateCcw className="w-4 h-4" />
                          Retry
                        </>
                      )}
                    </Button>
                  )}
              </div>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1 font-medium">Current Version</p>
              <WorkspaceVersionStatus
                workspace={workspace}
                className="text-sm font-mono text-foreground"
              />
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1 font-medium">Environment</p>
              <p className="text-sm text-foreground">{envLabel}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1 font-medium">Infra</p>
              <p className="text-sm font-mono text-foreground">{workspace.infra || '—'}</p>
            </div>
            {isOnPrem && (
              <>
                <div>
                  <p className="text-xs text-muted-foreground mb-1 font-medium">Cluster</p>
                  <p className="text-sm font-mono text-foreground">
                    {workspace.clusterName || '—'}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1 font-medium">Delivery Engine</p>
                  <p className="text-sm text-foreground">Duplo extensions</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1 font-medium">Terraform</p>
                  <p className="text-sm text-foreground">{duploTenant?.status ?? '—'}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1 font-medium">Release (Flux)</p>
                  <p className="text-sm text-foreground">
                    {duploTenant?.releaseStatus ?? 'Not reported'}
                  </p>
                </div>
              </>
            )}
            {forgeUrl && statusKey === 'WORKSPACE_STATUS_HEALTHY' && (
              <div>
                <p className="text-xs text-muted-foreground mb-1 font-medium">X-Forge</p>
                <a
                  href={forgeUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-sm text-blue-600 dark:text-blue-400 hover:underline"
                >
                  Open in X-Forge
                  <ExternalLink className="w-3.5 h-3.5" />
                </a>
              </div>
            )}
            <div>
              <p className="text-xs text-muted-foreground mb-1 font-medium">Last Updated</p>
              <p className="text-sm text-foreground">
                {workspace.updatedAt
                  ? timestampDate(workspace.updatedAt).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                    })
                  : '—'}
              </p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1 font-medium">Last Promoted</p>
              <p className="text-sm text-foreground">
                {workspace.lastPromotedAt
                  ? formatDateTime(timestampDate(workspace.lastPromotedAt))
                  : '—'}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={(v) => setSearchParams({ tab: v })}>
        <TabsList>
          <TabsTrigger value="components">Components</TabsTrigger>
          <TabsTrigger value="health">Health</TabsTrigger>
          <TabsTrigger value="upgrade" className="gap-1.5">
            Deploy & Upgrade
            {hasUpgrade && (
              <span className="w-1.5 h-1.5 rounded-full bg-primary/60 animate-pulse" />
            )}
          </TabsTrigger>
          <TabsTrigger value="history">History</TabsTrigger>
        </TabsList>

        {/* Components tab */}
        <TabsContent value="components" className="mt-6 animate-in fade-in duration-300">
          {noCurrentRelease ? (
            <DeployedComponentsPanel
              manifest={workspace.deployedReleaseManifest}
              familyByServiceName={familyByServiceName}
              canDeployFamily={canDeployFamily}
              onDeployFamily={handleDeployFamily}
            />
          ) : (
            <ComponentsDiffPanel
              workspaceId={workspaceId}
              token={token}
              deployedManifest={workspace.deployedReleaseManifest}
              familyByServiceName={familyByServiceName}
              canDeployFamily={canDeployFamily}
              onDeployFamily={handleDeployFamily}
              currentReleaseId={workspace.currentReleaseId}
              currentVersion={workspace.currentVersion}
              availableReleases={availableReleases}
              active={activeTab === 'components'}
            />
          )}
        </TabsContent>

        {/* Health tab */}
        <TabsContent value="health" className="mt-6 animate-in fade-in duration-300">
          <ServiceHealthPanel checks={serviceHealth?.checks} isLoading={serviceHealthLoading} />
        </TabsContent>

        {/* Deploy & Upgrade tab */}
        <TabsContent value="upgrade" className="mt-6 animate-in fade-in duration-300">
          <WorkspaceDeployUpgradePanel
            currentVersion={workspace.currentVersion}
            nextRelease={nextRelease}
            showPreviewUpgradeButton={showPreviewUpgradeButton}
            onPreviewRelease={onPreviewRelease}
            hasPermission={hasPermission}
            onOpenDeployDialog={setActiveDeployDialog}
            workflowRunning={isWorkflowRunning}
          />
        </TabsContent>

        {/* History tab */}
        <TabsContent value="history" className="mt-6">
          <AuditHistory
            events={auditEvents}
            onLoadMore={fetchNextPage}
            hasMore={hasNextPage}
            isLoadingMore={isFetchingNextPage}
          />
        </TabsContent>
      </Tabs>

      <DeleteConfirmationDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        resourceName={workspace.displayName}
        resourceType="workspace"
        supportsForceTombstone={true}
        requireTypedConfirmation={workspace.slug || workspace.displayName}
        onConfirm={handleDelete}
        isDeleting={isDeleting}
      />

      <DeployBaisDialog
        workspaceId={workspaceId}
        open={activeDeployDialog === 'bais'}
        onOpenChange={(open) => setActiveDeployDialog(open ? 'bais' : null)}
      />
      <DeployMaiDialog
        workspaceId={workspaceId}
        open={activeDeployDialog === 'mai'}
        onOpenChange={(open) => setActiveDeployDialog(open ? 'mai' : null)}
      />
      <SingleServiceDeployDialog
        workspaceId={workspaceId}
        open={activeDeployDialog === 'single-service'}
        onOpenChange={(open) => setActiveDeployDialog(open ? 'single-service' : null)}
      />
    </div>
  );
}
